// Shared map style catalog for the 2D (Leaflet) and 3D (Mapbox GL) maps.
// Every style resolves to a working tile source even without a Mapbox token:
// token-less styles fall back to free providers (CARTO / Esri / OpenTopoMap / OSM).

export const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

const MAPBOX_ATTR = '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const OSM_ATTR = '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const CARTO_ATTR = '&copy; OpenStreetMap &copy; <a href="https://carto.com/attributions">CARTO</a>';
const ESRI_ATTR = 'Tiles &copy; Esri';

const mapboxUrl = (styleId) =>
  `https://api.mapbox.com/styles/v1/${styleId}/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`;

const mapboxTile = (styleId, label) => ({
  label,
  url: mapboxUrl(styleId),
  attribution: MAPBOX_ATTR,
  tileSize: 512,
  zoomOffset: -1,
  maxZoom: 22,
  maxNativeZoom: 22,
  free: false,
});

// Free fallbacks matching each visual style
const FREE_STYLES = {
  Streets: {
    label: 'Streets',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    attribution: CARTO_ATTR,
    maxZoom: 20,
    maxNativeZoom: 19,
    free: true,
  },
  Hybrid: {
    label: 'Hybrid',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: ESRI_ATTR,
    maxZoom: 22,
    maxNativeZoom: 19,
    free: true,
  },
  Terrain: {
    label: 'Terrain',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: `${OSM_ATTR} &copy; <a href="https://opentopomap.org">OpenTopoMap</a>`,
    maxZoom: 17,
    maxNativeZoom: 17,
    free: true,
  },
  Dark: {
    label: 'Dark',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: CARTO_ATTR,
    maxZoom: 20,
    maxNativeZoom: 19,
    free: true,
  },
  NavigationPreviewNight: {
    label: 'Navigation Night',
    url: 'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png',
    attribution: CARTO_ATTR,
    maxZoom: 20,
    maxNativeZoom: 19,
    free: true,
  },
};

// With a Mapbox token the premium styles are used; otherwise the free twins.
export const MAP_STYLES = MAPBOX_TOKEN
  ? {
      Streets: mapboxTile('mapbox/streets-v11', 'Streets'),
      Hybrid: mapboxTile('mapbox/satellite-streets-v11', 'Hybrid'),
      Terrain: mapboxTile('mapbox/outdoors-v11', 'Terrain'),
      Dark: mapboxTile('mapbox/dark-v11', 'Dark'),
      Navigation: mapboxTile('mapbox/navigation-day-v1', 'Navigation'),
      NavigationPreviewNight: mapboxTile('mapbox/navigation-night-v1', 'Navigation Night'),
      OpenStreetMap: {
        label: 'OpenStreetMap (Free)',
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: OSM_ATTR,
        maxZoom: 20,
        maxNativeZoom: 19,
        free: true,
      },
      SatelliteFree: {
        label: 'Satellite (Esri Free)',
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attribution: ESRI_ATTR,
        maxZoom: 22,
        maxNativeZoom: 19,
        free: true,
      },
    }
  : {
      ...FREE_STYLES,
      OpenStreetMap: {
        label: 'OpenStreetMap (Free)',
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: OSM_ATTR,
        maxZoom: 20,
        maxNativeZoom: 19,
        free: true,
      },
    };

export const DEFAULT_MAP_STYLE = 'Dark';

// The 3D renderer (Mapbox GL) only understands these style keys.
const STYLE_3D_FALLBACKS = {
  Streets: 'Streets',
  Hybrid: 'Hybrid',
  Terrain: 'Terrain',
  Dark: 'Dark',
  Navigation: 'Navigation',
  NavigationPreviewNight: 'NavigationPreviewNight',
  OpenStreetMap: 'Streets',
  SatelliteFree: 'Hybrid',
};

export function to3DStyleKey(styleKey) {
  return STYLE_3D_FALLBACKS[styleKey] || 'NavigationPreviewNight';
}
